import { getAdminItemsQuery } from '@/api/items/adminItemQueries';
import {
  createProtocolTypeItemMutation,
  createProtocolTypeMutation,
  deleteProtocolTypeItemMutation,
  deleteProtocolTypeMutation,
  updateProtocolTypeMutation,
} from '@/api/protocols/protocolMutations';
import { getProtocolQuery } from '@/api/protocols/protocolQueries';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { Select } from '@/components/common/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import { Textarea } from '@/components/common/Textarea';
import type {
  Protocol,
  ProtocolType,
  ProtocolTypeItem,
} from '@/domain/Protocol';
import { getStorageUrl } from '@/lib/api';
import { protocolSchema } from '@/validation/protocolValidator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Upload } from 'lucide-react';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { toFormikValidate } from 'zod-formik-adapter';

interface ProtocolsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  protocol?: Protocol | null;
  mode: 'add' | 'edit';
  onSubmit: (data: Protocol, image?: File) => void;
  isPending?: boolean;
}

export function ProtocolsDialog({
  open,
  onOpenChange,
  protocol,
  mode,
  onSubmit,
  isPending,
}: ProtocolsDialogProps) {
  const [newMechanismName, setNewMechanismName] = useState('');
  const [selectedProtocolTypeId, setSelectedProtocolTypeId] = useState<
    string | undefined
  >();
  const [selectedSupplementId, setSelectedSupplementId] = useState<
    string | undefined
  >();
  const [imageFile, setImageFile] = useState<File | undefined>();
  const [addingItemStep, setAddingItemStep] = useState<
    'selection' | 'dosage' | 'description'
  >('selection');
  const [tempDosage, setTempDosage] = useState('');
  const [tempDescription, setTempDescription] = useState('');
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = mode === 'edit';

  const { mutate: createType } = useMutation({
    ...createProtocolTypeMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.single(protocol?.id || 0),
      });
      toast.success('Mechanism added successfully');
      setNewMechanismName('');
    },
  });

  const { mutate: updateType } = useMutation({
    ...updateProtocolTypeMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.single(protocol?.id || 0),
      });
      toast.success('Mechanism updated successfully');
    },
  });

  const { mutate: deleteType } = useMutation({
    ...deleteProtocolTypeMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.single(protocol?.id || 0),
      });
      toast.success('Mechanism removed successfully');
    },
  });

  const { mutate: createItem } = useMutation({
    ...createProtocolTypeItemMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.single(protocol?.id || 0),
      });
      toast.success('Item added successfully');
      setSelectedSupplementId(undefined);
      setAddingItemStep('selection');
      setTempDosage('');
      setTempDescription('');
    },
  });

  const { mutate: deleteItem } = useMutation({
    ...deleteProtocolTypeItemMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.single(protocol?.id || 0),
      });
      toast.success('Item removed successfully');
    },
  });

  const { data: protocolDetails } = useQuery({
    ...getProtocolQuery(protocol?.id || 0),
    enabled: open && isEditMode && !!protocol?.id,
  });

  const { data: itemsResponse } = useQuery({
    ...getAdminItemsQuery(),
    enabled: open,
  });

  const items = itemsResponse?.data || [];
  const currentProtocol = (
    isEditMode ? protocolDetails || protocol : null
  ) as Protocol | null;
  const initialValues = useMemo(
    () => ({
      name: currentProtocol?.name || '',
      type: currentProtocol?.type || '',
      type_explanation: currentProtocol?.type_explanation || '',
      protocol_types: currentProtocol?.protocol_types || [],
    }),
    [currentProtocol],
  );

  const validate = useMemo(() => toFormikValidate(protocolSchema), []);

  const formik = useFormik({
    initialValues,
    validate,
    enableReinitialize: true,
    onSubmit: (values) => {
      const updatedProtocol: Protocol = {
        id: protocol?.id || 0,
        name: values.name,
        type: values.type,
        image_id: protocol?.image_id,
        image_url: protocol?.image_url,
        type_explanation: values.type_explanation,
        protocol_types: values.protocol_types,
      };
      onSubmit(updatedProtocol, imageFile);
    },
  });

  useEffect(() => {
    if (open) {
      setNewMechanismName('');
      setSelectedProtocolTypeId(undefined);
      setSelectedSupplementId(undefined);
      setImageFile(undefined);
      setAddingItemStep('selection');
      setTempDosage('');
      setTempDescription('');
    }
  }, [open]);

  const supplementItems = items.map((item) => ({
    id: item.id.toString(),
    value: item.name,
    label: item.name,
  }));

  const selectedItem = items.find(
    (i) => i.id.toString() === selectedSupplementId,
  );

  const dosageOptions =
    selectedItem?.strengths?.map((s) => ({
      id: s.id.toString(),
      value: s.strength,
      label: s.strength,
    })) || [];

  const protocolTypeOptions = formik.values.protocol_types.map(
    (pt: ProtocolType) => ({
      id: pt.id.toString(),
      value: pt.name,
      label: pt.name,
    }),
  );

  const handleAddMechanism = () => {
    if (!newMechanismName.trim()) return;

    if (isEditMode && protocol?.id) {
      createType({
        protocolId: protocol.id,
        data: { name: newMechanismName.trim() },
      });
    } else {
      const newType: ProtocolType = {
        id: Date.now(), // Temporary ID
        protocol_id: protocol?.id || 0,
        name: newMechanismName.trim(),
        items: [],
      };
      formik.setFieldValue('protocol_types', [
        ...formik.values.protocol_types,
        newType,
      ]);
      setNewMechanismName('');
    }
  };

  const handleUpdateMechanismName = (id: number, name: string) => {
    formik.setFieldValue(
      'protocol_types',
      formik.values.protocol_types.map((pt: ProtocolType) =>
        pt.id === id ? { ...pt, name } : pt,
      ),
    );
  };

  const handleAddItem = () => {
    if (!selectedProtocolTypeId || !selectedSupplementId || !selectedItem)
      return;

    const selectedStrength = selectedItem.strengths?.find(
      (s) => s.strength === tempDosage,
    );
    const strengthId = selectedStrength?.id ?? selectedItem.id;

    if (isEditMode && protocol?.id) {
      createItem({
        protocolId: protocol.id,
        typeId: Number(selectedProtocolTypeId),
        data: {
          item_id: strengthId,
          dosage: tempDosage,
          description: tempDescription,
        },
      });
    } else {
      const newItem: ProtocolTypeItem = {
        id: Date.now(),
        protocol_id: protocol?.id || 0,
        protocol_type_id: Number(selectedProtocolTypeId),
        item_id: strengthId,
        dosage: tempDosage,
        description: tempDescription,
        item: selectedItem as ProtocolTypeItem['item'],
      };

      formik.setFieldValue(
        'protocol_types',
        formik.values.protocol_types.map((pt: ProtocolType) => {
          if (pt.id.toString() === selectedProtocolTypeId) {
            return { ...pt, items: [...pt.items, newItem] };
          }
          return pt;
        }),
      );
      setSelectedSupplementId(undefined);
      setAddingItemStep('selection');
      setTempDosage('');
      setTempDescription('');
    }
  };

  const handleRemoveItem = (typeId: number, itemId: number) => {
    if (isEditMode && protocol?.id) {
      const type = formik.values.protocol_types.find((t) => t.id === typeId);
      const isLastItem = type?.items.length === 1;

      deleteItem(
        {
          protocolId: protocol.id,
          typeId,
          id: itemId,
        },
        {
          onSuccess: () => {
            if (isLastItem) {
              deleteType({ protocolId: protocol.id, id: typeId });
            }
          },
        },
      );
    } else {
      const updatedTypes = formik.values.protocol_types
        .map((pt: ProtocolType) => {
          if (pt.id === typeId) {
            return {
              ...pt,
              items: pt.items.filter((i: ProtocolTypeItem) => i.id !== itemId),
            };
          }
          return pt;
        })
        .filter((pt: ProtocolType) => pt.items.length > 0);

      formik.setFieldValue('protocol_types', updatedTypes);
    }
  };

  const dialogTitle = isEditMode ? 'Edit Protocol' : 'Add Protocol';

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      maxWidth="4xl"
    >
      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
        <Input
          id="name"
          name="name"
          label="Enter name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.name}
          touched={formik.touched.name}
          className="bg-white"
        />

        <div className="flex flex-col gap-2">
          <label className="text-table-foreground text-sm font-medium">
            Protocol Image
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="border-input flex size-10 items-center justify-center rounded-lg border bg-white hover:bg-slate-50"
              title="Upload image"
            >
              <Upload className="size-5 text-slate-500" />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setImageFile(file);
                }
              }}
            />
            {(imageFile || protocol?.image_url) && (
              <div className="flex items-center gap-3 overflow-hidden rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="size-10 shrink-0 overflow-hidden rounded border border-slate-100 bg-white">
                  <img
                    src={
                      imageFile
                        ? URL.createObjectURL(imageFile)
                        : getStorageUrl(protocol?.image_url || '')
                    }
                    alt="Preview"
                    className="size-full object-cover"
                  />
                </div>
                <span className="max-w-[200px] truncate text-xs text-slate-500">
                  {imageFile ? imageFile.name : protocol?.image_url}
                </span>
              </div>
            )}
          </div>
        </div>

        <Input
          id="type"
          name="type"
          label="Enter type"
          value={formik.values.type}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.type}
          touched={formik.touched.type}
          className="bg-white"
        />

        <Textarea
          id="type_explanation"
          name="type_explanation"
          label="Enter explanation"
          value={formik.values.type_explanation}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.type_explanation}
          touched={formik.touched.type_explanation}
          className="min-h-[100px] bg-white"
        />

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Input
              id="mechanisms"
              label="Mechanisms targeted"
              value={newMechanismName}
              onChange={(e) => setNewMechanismName(e.target.value)}
              className="bg-white"
            />
          </div>
          <Button type="button" variant="primary" onClick={handleAddMechanism}>
            Add
          </Button>
        </div>

        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-medium">Mechanisms</h3>

          <div className="flex flex-col gap-4">
            {formik.values.protocol_types.map((pt: ProtocolType) => (
              <div key={pt.id} className="flex items-start gap-4">
                <div className="flex-1">
                  <Input
                    value={pt.name}
                    onChange={(e) =>
                      handleUpdateMechanismName(pt.id, e.target.value)
                    }
                    className="bg-white"
                    containerClassName="!p-0"
                  />
                </div>
                <div className="flex flex-col pt-2 text-sm">
                  <RelationActionButton
                    type="button"
                    onClick={() => {
                      if (isEditMode && protocol?.id) {
                        deleteType({ protocolId: protocol.id, id: pt.id });
                      } else {
                        formik.setFieldValue(
                          'protocol_types',
                          formik.values.protocol_types.filter(
                            (p: ProtocolType) => p.id !== pt.id,
                          ),
                        );
                      }
                    }}
                    className="h-auto py-0 text-left text-sm!"
                  >
                    Remove
                  </RelationActionButton>
                  <RelationActionButton
                    type="button"
                    onClick={() => {
                      if (isEditMode && protocol?.id) {
                        updateType({
                          protocolId: protocol.id,
                          id: pt.id,
                          data: { name: pt.name },
                        });
                      } else {
                        toast.success('Mechanism name saved locally');
                      }
                    }}
                    className="h-auto py-0 text-left text-sm! font-semibold"
                  >
                    Save
                  </RelationActionButton>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-4">
            <Combobox
              items={protocolTypeOptions}
              label="Select type to add items"
              value={selectedProtocolTypeId}
              onChange={setSelectedProtocolTypeId}
              className="bg-white"
            />

            {selectedProtocolTypeId && addingItemStep === 'selection' && (
              <Combobox
                items={supplementItems}
                label="Select supplement to add"
                value={selectedSupplementId}
                onChange={(val) => {
                  setSelectedSupplementId(val);
                  if (val) setAddingItemStep('dosage');
                }}
                className="bg-white"
              />
            )}

            {selectedProtocolTypeId && addingItemStep === 'dosage' && (
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Select
                    options={dosageOptions}
                    label="Select supplement dose"
                    value={tempDosage}
                    onChange={setTempDosage}
                    className="bg-white"
                  />
                </div>
                <div className="flex flex-col pt-2 text-sm">
                  <RelationActionButton
                    type="button"
                    disabled={!tempDosage}
                    onClick={() => setAddingItemStep('description')}
                    className="h-auto py-0 text-left !text-sm font-semibold disabled:opacity-50"
                  >
                    Add dose
                  </RelationActionButton>
                  <RelationActionButton
                    type="button"
                    onClick={() => {
                      setAddingItemStep('selection');
                      setSelectedSupplementId(undefined);
                    }}
                    className="h-auto py-0 text-left text-sm!"
                  >
                    Back
                  </RelationActionButton>
                </div>
              </div>
            )}

            {selectedProtocolTypeId && addingItemStep === 'description' && (
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Textarea
                    label="Enter description"
                    value={tempDescription}
                    onChange={(e) => setTempDescription(e.target.value)}
                    className="min-h-[100px] bg-white"
                  />
                </div>
                <div className="flex flex-col pt-2 text-sm">
                  <RelationActionButton
                    type="button"
                    onClick={handleAddItem}
                    className="h-auto py-0 text-left text-sm! font-semibold"
                  >
                    Add item
                  </RelationActionButton>
                  <RelationActionButton
                    type="button"
                    onClick={() => setAddingItemStep('dosage')}
                    className="h-auto py-0 text-left text-sm!"
                  >
                    Back
                  </RelationActionButton>
                </div>
              </div>
            )}
          </div>

          <Table className="mt-4">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Type</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="w-[100px]">Dosage</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formik.values.protocol_types.map((pt: ProtocolType) => (
                <React.Fragment key={pt.id}>
                  {pt.items.length > 0 ? (
                    pt.items.map((item: ProtocolTypeItem, idx: number) => {
                      const nestedItem = item.item as
                        | { strength?: string; name?: string }
                        | null
                        | undefined;
                      const dosage = nestedItem?.strength || item.dosage || '';
                      const description = item.description || '';
                      const itemName = nestedItem?.name || '';

                      return (
                        <TableRow key={item.id}>
                          <TableCell className="truncate">
                            {idx === 0 ? pt.name : ''}
                          </TableCell>
                          <TableCell className="truncate font-medium">
                            {itemName}
                          </TableCell>
                          <TableCell className="truncate">{dosage}</TableCell>
                          <TableCell className="truncate text-slate-500">
                            {description}
                          </TableCell>
                          <TableCell>
                            <RelationActionButton
                              type="button"
                              onClick={() => handleRemoveItem(pt.id, item.id)}
                              className="text-delete text-xs!"
                            >
                              Remove
                            </RelationActionButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow key={pt.id}>
                      <TableCell className="truncate">{pt.name}</TableCell>
                      <TableCell
                        colSpan={3}
                        className="text-muted-foreground italic"
                      >
                        No items added
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 flex items-center justify-start gap-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={isPending || !formik.isValid}
          >
            Save changes
          </Button>
        </div>
      </form>
    </DialogWrapper>
  );
}
